/* ==========================================================================
   HVG Könyvek - global.js
   Vanilla only. One custom element per behaviour, registered at the bottom.
   Conventions: see THEME_CONVENTIONS.md section 4.
   ========================================================================== */

const HVG = {
  /* Announce a message to screen readers via the layout's live region. */
  announce(message) {
    const region = document.getElementById('HvgLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  },

  debounce(fn, wait = 300) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  },

  formatMoney(cents) {
    const format = document.documentElement.dataset.moneyFormat;
    if (!format) return String(cents);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, () =>
      new Intl.NumberFormat(document.documentElement.lang || 'hu', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(cents / 100))
    );
  },
};

/* --------------------------------------------------------------------------
   Focus trapping - shared by every dialog and drawer
   -------------------------------------------------------------------------- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])';

class FocusTrap {
  constructor(container) {
    this.container = container;
    this.previouslyFocused = null;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  activate() {
    this.previouslyFocused = document.activeElement;
    this.container.addEventListener('keydown', this.handleKeydown);
    const first = this.container.querySelector(FOCUSABLE);
    if (first) first.focus();
  }

  deactivate() {
    this.container.removeEventListener('keydown', this.handleKeydown);
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
    this.previouslyFocused = null;
  }

  handleKeydown(event) {
    if (event.key !== 'Tab') return;
    const nodes = Array.from(this.container.querySelectorAll(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null
    );
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

let scrollLocks = 0;

function lockScroll() {
  scrollLocks += 1;
  if (scrollLocks === 1) {
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = width > 0 ? `${width}px` : '';
  }
}

function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

/* --------------------------------------------------------------------------
   Cart - the single owner of every Cart AJAX call
   -------------------------------------------------------------------------- */

const HvgCart = {
  routes: {
    add: '/cart/add.js',
    change: '/cart/change.js',
    get: '/cart.js',
  },

  init() {
    const root = document.querySelector('[data-cart-routes]');
    if (!root) return;
    try {
      Object.assign(this.routes, JSON.parse(root.textContent));
    } catch (error) {
      /* keep the defaults if the JSON payload is malformed */
    }
  },

  async request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ...body, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Cart request failed');
    }
    return data;
  },

  async add(items) {
    const cart = await this.request(this.routes.add, { items });
    await this.broadcast();
    return cart;
  },

  async change(line, quantity) {
    const cart = await this.request(this.routes.change, { line, quantity });
    this.publish(cart);
    return cart;
  },

  async fetch() {
    const response = await fetch(this.routes.get, { headers: { Accept: 'application/json' } });
    return response.json();
  },

  async broadcast() {
    this.publish(await this.fetch());
  },

  publish(cart) {
    document.dispatchEvent(new CustomEvent('hvg:cart:updated', { detail: { cart } }));
  },
};

/* --------------------------------------------------------------------------
   Cart count badge in the header
   -------------------------------------------------------------------------- */

class HvgCartCount extends HTMLElement {
  connectedCallback() {
    this.onUpdate = this.onUpdate.bind(this);
    document.addEventListener('hvg:cart:updated', this.onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('hvg:cart:updated', this.onUpdate);
  }

  onUpdate(event) {
    const count = event.detail.cart.item_count;
    this.textContent = count;
    this.hidden = count === 0;
    if (this.dataset.label) {
      this.setAttribute('aria-label', this.dataset.label.replace('[count]', count));
    }
  }
}

/* --------------------------------------------------------------------------
   Add to cart form
   -------------------------------------------------------------------------- */

class HvgProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-add-to-cart]');
    this.errorTarget = this.querySelector('[data-cart-error]');
    if (!this.form) return;
    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(this.form);
    // A sticky bar outside the form can contribute a second quantity field via
    // form=. The one the shopper last touched is the one we want, so take the
    // value from the control that fired rather than the first in tree order.
    const quantities = formData.getAll('quantity').filter((value) => value !== '');
    const submitter = event.submitter;
    let quantity = quantities.length > 0 ? quantities[quantities.length - 1] : 1;
    if (submitter && submitter.dataset.quantitySource) {
      const source = document.getElementById(submitter.dataset.quantitySource);
      if (source && source.value) quantity = source.value;
    }
    const item = { id: Number(formData.get('id')), quantity: Number(quantity) || 1 };
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    });
    if (Object.keys(properties).length > 0) item.properties = properties;

    const original = this.button.dataset.labelDefault || this.button.textContent.trim();
    this.setBusy(true);

    try {
      await HvgCart.add([item]);
      this.button.textContent = this.button.dataset.labelAdded || original;
      HVG.announce(this.button.dataset.announceAdded || '');
      window.setTimeout(() => {
        this.button.textContent = original;
      }, 2000);
      if (this.errorTarget) this.errorTarget.hidden = true;
    } catch (error) {
      if (this.errorTarget) {
        this.errorTarget.textContent = error.message;
        this.errorTarget.hidden = false;
      }
      HVG.announce(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(state) {
    this.button.classList.toggle('is-busy', state);
    this.button.setAttribute('aria-busy', String(state));
  }
}

/* --------------------------------------------------------------------------
   Quantity stepper
   -------------------------------------------------------------------------- */

class HvgQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.addEventListener('click', this.onClick);
    if (this.input) this.input.addEventListener('change', this.onChange);
    this.syncButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.input) this.input.removeEventListener('change', this.onChange);
  }

  onClick(event) {
    const button = event.target.closest('[data-quantity-step]');
    if (!button || !this.input) return;
    event.preventDefault();
    const step = Number(button.dataset.quantityStep);
    const min = Number(this.input.min || 1);
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(this.input.value || min) + step));
    if (next === Number(this.input.value)) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onChange() {
    this.syncButtons();
  }

  syncButtons() {
    if (!this.input) return;
    const value = Number(this.input.value || 0);
    const min = Number(this.input.min || 1);
    const max = this.input.max ? Number(this.input.max) : Infinity;
    this.querySelectorAll('[data-quantity-step]').forEach((button) => {
      const step = Number(button.dataset.quantityStep);
      button.disabled = step < 0 ? value <= min : value >= max;
    });
  }
}

/* --------------------------------------------------------------------------
   Cart line items - quantity change and remove on the cart page
   -------------------------------------------------------------------------- */

class HvgCartItems extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  onChange(event) {
    const input = event.target.closest('[data-line-quantity]');
    if (!input) return;
    this.update(Number(input.dataset.line), Number(input.value));
  }

  onClick(event) {
    const remove = event.target.closest('[data-line-remove]');
    if (!remove) return;
    event.preventDefault();
    this.update(Number(remove.dataset.line), 0);
  }

  async update(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    try {
      await HvgCart.change(line, quantity);
      window.location.reload();
    } catch (error) {
      HVG.announce(error.message);
      this.removeAttribute('aria-busy');
    }
  }
}

/* --------------------------------------------------------------------------
   Drawer - the mobile navigation panel
   -------------------------------------------------------------------------- */

class HvgDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.trap = this.panel ? new FocusTrap(this.panel) : null;
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick);

    this.openers = document.querySelectorAll(`[data-drawer-open="${this.id}"]`);
    this.openers.forEach((button) => button.addEventListener('click', this.onClick));
        // A natív contact form beküldés után teljes oldalbetöltéssel tér vissza.
    // Ha a modal sikeres / hibás állapotot renderelt, nyissuk újra magától,
    // hogy a látogató lássa az eredményt.
    if (this.querySelector('[data-modal-autoopen]')) {
      lockScroll();
      this.dialog.showModal();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.openers.forEach((button) => button.removeEventListener('click', this.onClick));
    if (this.open) unlockScroll();
  }

  onClick(event) {
    if (event.target.closest(`[data-drawer-open="${this.id}"]`)) {
      event.preventDefault();
      this.show();
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target === this) {
      event.preventDefault();
      this.hide();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.hide();
  }

  show() {
    if (this.open) return;
    this.open = true;
    this.hidden = false;
    this.setAttribute('data-open', 'true');
    this.openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    lockScroll();
    document.addEventListener('keydown', this.onKeydown);
    if (this.trap) this.trap.activate();
  }

  hide() {
    if (!this.open) return;
    this.open = false;
    this.removeAttribute('data-open');
    this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);
    if (this.trap) this.trap.deactivate();
    window.setTimeout(() => {
      if (!this.open) this.hidden = true;
    }, 300);
  }
}

/* --------------------------------------------------------------------------
   Modal - wraps a native <dialog> so every modal behaves the same
   -------------------------------------------------------------------------- */

class HvgModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.onClick = this.onClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClose);

    this.openers = document.querySelectorAll(`[data-modal-open="${this.id}"]`);
    this.openers.forEach((button) => button.addEventListener('click', this.onClick));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.dialog) this.dialog.removeEventListener('close', this.onClose);
    this.openers.forEach((button) => button.removeEventListener('click', this.onClick));
  }

  onClick(event) {
    if (event.target.closest(`[data-modal-open="${this.id}"]`)) {
      event.preventDefault();
      lockScroll();
      this.dialog.showModal();
      return;
    }
    if (event.target.closest('[data-modal-close]') || event.target === this.dialog) {
      this.dialog.close();
    }
  }

  onClose() {
    unlockScroll();
  }
}

/* --------------------------------------------------------------------------
   Navigation - hover intent on desktop, click/keyboard everywhere
   -------------------------------------------------------------------------- */

class HvgNavigation extends HTMLElement {
  connectedCallback() {
    this.closeDelay = Number(this.dataset.closeDelay || 260);
    this.timer = null;
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onPointerOut = this.onPointerOut.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  get isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  onPointerOver(event) {
    if (!this.isDesktop || event.pointerType === 'touch') return;
    const item = event.target.closest('[data-nav-item]');
    window.clearTimeout(this.timer);
    if (item && item.hasAttribute('data-nav-has-panel')) {
      this.openItem(item);
    } else if (item) {
      this.closeAll();
    }
  }

  onPointerOut(event) {
    if (!this.isDesktop || event.pointerType === 'touch') return;
    if (this.contains(event.relatedTarget)) return;
    this.scheduleClose();
  }

  onClick(event) {
    const toggle = event.target.closest('[data-nav-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const item = toggle.closest('[data-nav-item]');
    if (!item) return;
    if (item.getAttribute('data-open') === 'true') {
      this.closeAll();
    } else {
      this.openItem(item);
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('[data-nav-item][data-open="true"]');
    if (!open) return;
    this.closeAll();
    const toggle = open.querySelector('[data-nav-toggle]');
    if (toggle) toggle.focus();
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this.closeAll();
  }

  openItem(item) {
    this.closeAll();
    item.setAttribute('data-open', 'true');
    const toggle = item.querySelector('[data-nav-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  scheduleClose() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.closeAll(), this.closeDelay);
  }

  closeAll() {
    this.querySelectorAll('[data-nav-item][data-open="true"]').forEach((item) => {
      item.removeAttribute('data-open');
      const toggle = item.querySelector('[data-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }
}

/* --------------------------------------------------------------------------
   Predictive search
   -------------------------------------------------------------------------- */

class HvgSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.suggestions = this.querySelector('[data-search-suggestions]');
    this.panel = this.querySelector('[data-search-panel]');
    this.toggleButton = document.querySelector(`[data-search-toggle="${this.id}"]`);

    this.onInput = HVG.debounce(this.search.bind(this), 300);
    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    if (this.input) this.input.addEventListener('input', this.onInput);
    if (this.toggleButton) this.toggleButton.addEventListener('click', this.onToggle);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('input', this.onInput);
    if (this.toggleButton) this.toggleButton.removeEventListener('click', this.onToggle);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    if (this.controller) this.controller.abort();
  }

  onToggle(event) {
    event.preventDefault();
    if (this.getAttribute('data-open') === 'true') {
      this.close();
    } else {
      this.show();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.getAttribute('data-open') === 'true') this.close();
  }

  onDocumentClick(event) {
    if (this.getAttribute('data-open') !== 'true') return;
    if (this.contains(event.target)) return;
    if (this.toggleButton && this.toggleButton.contains(event.target)) return;
    this.close();
  }

  show() {
    this.setAttribute('data-open', 'true');
    this.hidden = false;
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'true');
      this.setToggleLabel('close');
    }
    if (this.input) this.input.focus();
  }

  setToggleLabel(state) {
    const label = this.toggleButton.dataset[state === 'close' ? 'labelClose' : 'labelOpen'];
    if (label) this.toggleButton.setAttribute('aria-label', label);
  }

  close() {
    this.removeAttribute('data-open');
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.setToggleLabel('open');
    }
    if (this.input) this.input.value = '';
    this.reset();
    this.hidden = true;
  }

  reset() {
    if (this.results) {
      this.results.innerHTML = '';
      this.results.hidden = true;
    }
    if (this.suggestions) this.suggestions.hidden = false;
  }

  async search() {
    const query = this.input ? this.input.value.trim() : '';
    if (query.length < 2) {
      this.reset();
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': this.dataset.resources || 'product',
      'resources[limit]': this.dataset.limit || '5',
      'resources[options][fields]': 'title,vendor,product_type,variants.title',
      section_id: this.dataset.sectionId || '',
    });

    try {
      const response = await fetch(`${this.dataset.searchUrl}?${params}`, {
        signal: this.controller.signal,
      });
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fragment = parsed.querySelector('[data-search-results]');
      if (this.results && fragment) {
        this.results.innerHTML = fragment.innerHTML;
        this.results.hidden = false;
        if (this.suggestions) this.suggestions.hidden = true;
        HVG.announce(this.results.dataset.announce || '');
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.reset();
    }
  }
}

/* --------------------------------------------------------------------------
   Tabs - the homepage product tab group
   -------------------------------------------------------------------------- */

class HvgTabs extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (tab) this.select(tab);
  }

  onKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    const tabs = this.tabs;
    const index = tabs.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (event.key === 'Home') next = tabs[0];
    if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    this.select(next);
    next.focus();
  }

  select(tab) {
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  }
}

/* --------------------------------------------------------------------------
   Variant picker
   -------------------------------------------------------------------------- */

class HvgVariantPicker extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
    } catch (error) {
      this.variants = [];
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange() {
    const selected = Array.from(this.querySelectorAll('input:checked, select')).map(
      (field) => field.value
    );
    const variant = this.variants.find(
      (candidate) =>
        candidate.options.length === selected.length &&
        candidate.options.every((option, index) => option === selected[index])
    );

    const url = new URL(window.location.href);
    if (variant) {
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    this.dispatchEvent(
      new CustomEvent('hvg:variant:changed', { bubbles: true, detail: { variant } })
    );
    this.render(variant);
  }

  async render(variant) {
    const sectionId = this.dataset.sectionId;
    const productUrl = this.dataset.productUrl;
    if (!sectionId || !productUrl || !variant) return;

    try {
      const response = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
      if (!response.ok) return;
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');

      document.querySelectorAll('[data-variant-target]').forEach((target) => {
        const key = target.dataset.variantTarget;
        const source = parsed.querySelector(`[data-variant-target="${key}"]`);
        if (source) target.innerHTML = source.innerHTML;
      });
    } catch (error) {
      // A failed re-render leaves the last good price and availability on screen,
      // which is better than an unhandled rejection and a half-updated buy box.
    }
  }
}

/* --------------------------------------------------------------------------
   Sticky header
   -------------------------------------------------------------------------- */

class HvgStickyHeader extends HTMLElement {
  connectedCallback() {
    this.onScroll = this.onScroll.bind(this);
    this.lastY = window.scrollY;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.setOffset();
    this.resizeObserver = new ResizeObserver(() => this.setOffset());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  setOffset() {
    document.documentElement.style.setProperty('--header-offset', `${this.offsetHeight}px`);
  }

  onScroll() {
    const y = window.scrollY;
    this.toggleAttribute('data-scrolled', y > 12);
    this.lastY = y;
  }
}

/* --------------------------------------------------------------------------
   Parallax - opt in per element, disabled when motion is reduced or off
   -------------------------------------------------------------------------- */

class HvgParallax extends HTMLElement {
  connectedCallback() {
    const motion = document.documentElement.dataset.motion;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (motion === 'off' || reduced) return;

    this.range = Number(this.dataset.range || 60) * (motion === 'calm' ? 0.45 : 1);
    this.target = this.querySelector('[data-parallax-layer]');
    if (!this.target) return;

    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.update();
    });
    this.observer.observe(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
  }

  onScroll() {
    if (!this.visible || this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const rect = this.getBoundingClientRect();
    const progress = (rect.top + rect.height) / (window.innerHeight + rect.height);
    const offset = (0.5 - progress) * this.range;
    this.target.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
  }
}

/* --------------------------------------------------------------------------
   Product media gallery - a scroll-snap track driven by arrows and dots
   -------------------------------------------------------------------------- */

class HvgGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-swipe]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.onClick = this.onClick.bind(this);
    this.onScroll = HVG.debounce(this.syncFromScroll.bind(this), 80);

    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
  }

  onClick(event) {
    const dot = event.target.closest('[data-gallery-dot]');
    if (dot) {
      this.goTo(Number(dot.dataset.index));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) this.goTo(this.index - 1);
    if (event.target.closest('[data-gallery-next]')) this.goTo(this.index + 1);
  }

  goTo(index) {
    const count = this.slides.length;
    const next = (index + count) % count;
    this.track.scrollTo({ left: this.track.clientWidth * next, behavior: 'smooth' });
    this.setIndex(next);
  }

  syncFromScroll() {
    const width = this.track.clientWidth;
    if (width === 0) return;
    this.setIndex(Math.round(this.track.scrollLeft / width));
  }

  setIndex(index) {
    this.index = index;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === index)));
  }
}

/* --------------------------------------------------------------------------
   Facets - submits the filter form on change. Without JS the noscript Apply
   button does the same thing, so filtering never depends on this.
   -------------------------------------------------------------------------- */

class HvgFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.onChange = HVG.debounce(this.submit.bind(this), 250);
    this.form.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('change', this.onChange);
  }

  submit() {
    const params = new URLSearchParams(new FormData(this.form));
    const cleaned = new URLSearchParams();
    params.forEach((value, key) => {
      if (value !== '') cleaned.append(key, value);
    });
    const query = cleaned.toString();
    window.location.href = query ? `${this.form.action}?${query}` : this.form.action;
  }
}

/* --------------------------------------------------------------------------
   Confirm before submitting - wraps a form/button, reads its message from
   data-confirm-message. Used for destructive actions like deleting an address.
   Without JS the wrapped form still submits, it just skips the confirmation.
   -------------------------------------------------------------------------- */

class HvgConfirmSubmit extends HTMLElement {
  connectedCallback() {
    this.onSubmit = this.onSubmit.bind(this);
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
  }

  onSubmit(event) {
    const message = this.dataset.confirmMessage;
    if (message && !window.confirm(message)) {
      event.preventDefault();
    }
  }
}

/* --------------------------------------------------------------------------
   Address form - syncs the province field to the selected country, reading
   each option's `data-provinces` JSON. Falls back to a plain text input when
   JS is unavailable or the country has no known provinces.
   -------------------------------------------------------------------------- */

class HvgAddressForm extends HTMLElement {
  connectedCallback() {
    this.countrySelect = this.querySelector('[data-address-country]');
    this.provinceControl = this.querySelector('[data-address-province-control]');
    if (!this.countrySelect || !this.provinceControl) return;

    const currentField = this.provinceControl.querySelector('input, select');
    this.initialProvince = currentField ? currentField.value : '';
    this.fieldId = currentField ? currentField.id : '';
    this.fieldName = currentField ? currentField.name : 'address[province]';

    const currentCountry = this.countrySelect.dataset.currentCountry;
    if (currentCountry) {
      const match = Array.from(this.countrySelect.options).find(
        (option) => option.value === currentCountry
      );
      if (match) this.countrySelect.value = currentCountry;
    }

    this.onCountryChange = this.onCountryChange.bind(this);
    this.countrySelect.addEventListener('change', this.onCountryChange);
    this.renderProvince(this.initialProvince);
  }

  disconnectedCallback() {
    if (this.countrySelect) this.countrySelect.removeEventListener('change', this.onCountryChange);
  }

  onCountryChange() {
    this.renderProvince('');
  }

  renderProvince(value) {
    const option = this.countrySelect.options[this.countrySelect.selectedIndex];
    if (!option) return;

    let provinces = [];
    try {
      provinces = JSON.parse(option.dataset.provinces || '[]');
    } catch (error) {
      provinces = [];
    }

    this.provinceControl.innerHTML = '';

    if (provinces.length) {
      const select = document.createElement('select');
      select.className = 'select';
      select.id = this.fieldId;
      select.name = this.fieldName;
      select.setAttribute('autocomplete', 'address-level1');
      provinces.forEach(([provinceValue, label]) => {
        const optionEl = document.createElement('option');
        optionEl.value = provinceValue;
        optionEl.textContent = label;
        if (provinceValue === value) optionEl.selected = true;
        select.appendChild(optionEl);
      });
      this.provinceControl.appendChild(select);
      return;
    }

    const input = document.createElement('input');
    input.className = 'input';
    input.type = 'text';
    input.id = this.fieldId;
    input.name = this.fieldName;
    input.value = value;
    input.setAttribute('autocomplete', 'address-level1');
    this.provinceControl.appendChild(input);
  }
}

/* --------------------------------------------------------------------------
   Gift card - print trigger and QR code render
   -------------------------------------------------------------------------- */

class HvgGiftCardPrint extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    if (!event.target.closest('button')) return;
    window.print();
  }
}

class HvgGiftCardQr extends HTMLElement {
  connectedCallback() {
    const text = this.dataset.qrText;
    if (!text || typeof window.QRCode === 'undefined') return;
    new window.QRCode(this, { text, width: 140, height: 140 });
  }
}

/* --------------------------------------------------------------------------
   Copy link - announces success rather than only showing it, and falls back
   to a text selection when the async clipboard API is unavailable.
   -------------------------------------------------------------------------- */

class HvgCopyLink extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-copy-link]');
    if (!this.button) return;
    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  async onClick() {
    const url = this.dataset.copyUrl || window.location.href;
    const message = this.button.dataset.copySuccessLabel;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        document.body.removeChild(field);
      }
    } catch (error) {
      return;
    }

    if (message) {
      HVG.announce(message);
      const original = this.button.getAttribute('aria-label');
      this.button.setAttribute('aria-label', message);
      this.button.classList.add('is-copied');
      window.setTimeout(() => {
        this.button.setAttribute('aria-label', original);
        this.button.classList.remove('is-copied');
      }, 2000);
    }
  }
}

/* --------------------------------------------------------------------------
   Sticky add-to-cart bar - revealed once the buy box scrolls out of view.
   The artboards specify the bar but carry no show/hide logic, so this is the
   theme's own trigger.
   -------------------------------------------------------------------------- */

class HvgStickyAtc extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.watch;
    this.watched = selector ? document.querySelector(selector) : null;
    if (!this.watched) return;

    this.setVisible(false);
    this.observer = new IntersectionObserver(
      (entries) => this.setVisible(!entries[0].isIntersecting),
      { rootMargin: '0px 0px -80% 0px' }
    );
    this.observer.observe(this.watched);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.body.style.removeProperty('--sticky-atc-height');
  }

  setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    document.body.style.setProperty(
      '--sticky-atc-height',
      visible ? `${this.offsetHeight}px` : '0px'
    );
  }
}

/* --------------------------------------------------------------------------
   Product recommendations - fetched from the Product Recommendations API the
   first time the section approaches the viewport, so it costs nothing above
   the fold. Renders nothing when the API returns no products.
   -------------------------------------------------------------------------- */

class HvgRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) return;
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('hvg-recommendations');
      if (fresh && fresh.innerHTML.trim().length > 0) {
        this.innerHTML = fresh.innerHTML;
      }
    } catch (error) {
      /* recommendations are an enhancement - stay silent and render nothing */
    }
  }
}

/* --------------------------------------------------------------------------
   Manuscript form - the two enhancements the submission form needs on top of
   plain HTML: a live "n / max" counter under the pitch textarea, and the
   chosen file's name echoed into the upload zone. The genre chips are radio
   inputs, so their single-select behaviour is native and needs nothing here.
   Both label templates arrive through data- attributes, already translated.
   -------------------------------------------------------------------------- */

class HvgManuscriptForm extends HTMLElement {
  connectedCallback() {
    this.pitch = this.querySelector('[data-pitch]');
    this.counter = this.querySelector('[data-pitch-count]');
    this.file = this.querySelector('[data-manuscript-file]');
    this.fileName = this.querySelector('[data-manuscript-file-name]');
    this.fileNote = this.querySelector('[data-manuscript-file-note]');

    this.onPitchInput = this.onPitchInput.bind(this);
    this.onFileChange = this.onFileChange.bind(this);

    if (this.pitch && this.counter) {
      this.pitch.addEventListener('input', this.onPitchInput);
      this.onPitchInput();
    }

    if (this.file) this.file.addEventListener('change', this.onFileChange);
  }

  disconnectedCallback() {
    if (this.pitch) this.pitch.removeEventListener('input', this.onPitchInput);
    if (this.file) this.file.removeEventListener('change', this.onFileChange);
  }

  onPitchInput() {
    const max = Number(this.dataset.pitchMax) || 0;
    let value = this.pitch.value;

    /* maxlength already caps typing; this catches a paste in older browsers. */
    if (max > 0 && value.length > max) {
      value = value.slice(0, max);
      this.pitch.value = value;
    }

    const template = this.dataset.countTemplate || '';
    this.counter.textContent = template.replace('[count]', String(value.length));
  }

  onFileChange() {
    const chosen = this.file.files && this.file.files[0];

    if (this.fileName) {
      this.fileName.textContent = chosen ? chosen.name : this.fileName.dataset.emptyLabel || '';
    }

    if (this.fileNote) {
      const note = chosen ? this.fileNote.dataset.chosenLabel : this.fileNote.dataset.emptyLabel;
      this.fileNote.textContent = note || '';
    }

    this.classList.toggle('manuscript-form--has-file', Boolean(chosen));
  }
}

/* --------------------------------------------------------------------------
   Registration
   -------------------------------------------------------------------------- */

HvgCart.init();

const elements = {
  'hvg-cart-count': HvgCartCount,
  'hvg-product-form': HvgProductForm,
  'hvg-quantity': HvgQuantity,
  'hvg-cart-items': HvgCartItems,
  'hvg-drawer': HvgDrawer,
  'hvg-modal': HvgModal,
  'hvg-navigation': HvgNavigation,
  'hvg-search': HvgSearch,
  'hvg-tabs': HvgTabs,
  'hvg-variant-picker': HvgVariantPicker,
  'hvg-sticky-header': HvgStickyHeader,
  'hvg-parallax': HvgParallax,
  'hvg-gallery': HvgGallery,
  'hvg-facets': HvgFacets,
  'hvg-copy-link': HvgCopyLink,
  'hvg-sticky-atc': HvgStickyAtc,
  'hvg-recommendations': HvgRecommendations,
  'hvg-gift-card-print': HvgGiftCardPrint,
  'hvg-gift-card-qr': HvgGiftCardQr,
  'hvg-confirm-submit': HvgConfirmSubmit,
  'hvg-address-form': HvgAddressForm,
  'hvg-manuscript-form': HvgManuscriptForm,
};

Object.entries(elements).forEach(([tag, constructor]) => {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
});



(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const items = document.querySelectorAll('.header__nav-item[data-nav-has-panel]');
  items.forEach((li) => {
    const toggle = li.querySelector('[data-nav-toggle]');
    let closeTimer;
    const setOpen = (open) => {
      li.dataset.open = open ? 'true' : 'false';
      toggle?.setAttribute('aria-expanded', open);
    };
    li.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      items.forEach((other) => other !== li && other.dataset.open === 'true' &&
        (other.dataset.open = 'false', other.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'false')));
      setOpen(true);
    });
    li.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => setOpen(false), 150);
    });
  });
})();










(function () {
  const breakpoint = 990;

  function moveAccordions() {
    const layout = document.querySelector('.main-product__layout');
    const accordions = document.querySelector('.main-product__accordions');

    if (!layout || !accordions) return;

    if (window.innerWidth < breakpoint) {
      // Csak akkor helyezzük át, ha még nem a layout legvégén van
      if (accordions.parentElement !== layout || layout.lastElementChild !== accordions) {
        layout.appendChild(accordions);
      }
    }
  }

  // Betöltéskor és átméretezéskor
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', moveAccordions);
  } else {
    moveAccordions();
  }

  window.addEventListener('resize', moveAccordions);

  // Ha a Shopify vagy egy alkalmazás később tölti be a szekciót
  const observer = new MutationObserver(moveAccordions);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();